import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client'
import { API_ROOT, APP_VERSION } from 'env'
import { getToken, removeToken } from './token'

const defaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
}

const achievementsHttpLink = createHttpLink({
  uri: operation => `${API_ROOT}/graphql?op=${operation.operationName}`,
})

const AUTH_ERROR_MESSAGES = ['session expired', 'invalid authentication token']
const UNAUTHENTICATED_ERROR_CODE = 'UNAUTHENTICATED'

const getErrorStatusCode = error => {
  if (!error) return null
  if (error.statusCode) return error.statusCode
  if (error.status) return error.status
  if (error.response?.status) return error.response.status
  return null
}

export const isAuthError = ({ graphQLErrors = [], networkError } = {}) => {
  const hasAuthGraphQLError = graphQLErrors.some(graphQLError => {
    const errorCode = graphQLError?.extensions?.code
    if (
      typeof errorCode === 'string' &&
      errorCode.toUpperCase() === UNAUTHENTICATED_ERROR_CODE
    ) {
      return true
    }

    const message = graphQLError?.message?.toLowerCase() || ''
    return AUTH_ERROR_MESSAGES.some(authMessage =>
      message.includes(authMessage),
    )
  })

  if (hasAuthGraphQLError) return true

  const statusCode = getErrorStatusCode(networkError)
  if (statusCode === 401) return true

  const networkMessage = networkError?.message?.toLowerCase() || ''
  return AUTH_ERROR_MESSAGES.some(authMessage =>
    networkMessage.includes(authMessage),
  )
}

export const buildAuthErrorPayload = ({ error, operationName } = {}) => ({
  message: error?.message || 'Authentication session expired',
  statusCode: getErrorStatusCode(error),
  operationName: operationName || null,
})

export const reportAchievementsAuthError = ({ error, operationName } = {}) => {
  if (typeof window === 'undefined' || !window.rg4js) return

  const payload = buildAuthErrorPayload({ error, operationName })

  window.rg4js('send', {
    error: new Error(payload.message),
    customData: {
      statusCode: payload.statusCode,
      operationName: payload.operationName,
    },
    tags: ['achievements-auth'],
  })
}

export const handleAchievementsAuthError = ({ error, operationName } = {}) => {
  removeToken()
  reportAchievementsAuthError({ error, operationName })
  if (
    typeof window !== 'undefined' &&
    window.location.pathname !== '/achievements/login'
  ) {
    window.location.assign('/achievements/login')
  }
}

export const createAchievementsErrorLink = () =>
  new ApolloLink(
    (operation, forward) =>
      new Observable(observer => {
        const subscription = forward(operation).subscribe({
          next: result => {
            if (isAuthError({ graphQLErrors: result?.errors })) {
              const authError =
                result?.errors?.[0] ||
                new Error('Authentication session expired')
              handleAchievementsAuthError({
                error: authError,
                operationName: operation.operationName,
              })
            }
            observer.next(result)
          },
          error: networkError => {
            if (isAuthError({ networkError })) {
              handleAchievementsAuthError({
                error: networkError,
                operationName: operation.operationName,
              })
            }
            observer.error(networkError)
          },
          complete: () => observer.complete(),
        })

        return () => subscription.unsubscribe()
      }),
  )

const achievementsAuthLink = new ApolloLink((operation, forward) => {
  const { skipAuth } = operation.getContext()
  const token = !skipAuth && getToken()

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...(token && { authorization: `Bearer ${token}` }),
      'X-Client-Version': APP_VERSION,
      'X-Client-Name': 'achievements-portal',
    },
  }))
  return forward(operation)
})

export const achievementsLink = createAchievementsErrorLink().concat(
  achievementsAuthLink.concat(achievementsHttpLink),
)

export const achievementsApolloClient = new ApolloClient({
  link: achievementsLink,
  cache: new InMemoryCache({ resultCaching: false }),
  defaultOptions,
})

export default achievementsApolloClient
